import { AppointmentResponse } from "@9amhealth/openapi";
import { isDeprecatedAppointmentType, Modal } from "@9amhealth/shared";
import { useBloc, useBlocNext } from "@blac/react";
import { Blac } from "blac-next";
import Scheduler from "molecule/scheduler/Scheduler";
import {
  ScheduleAppointmentTypes,
  SchedulerBloc,
  SchedulerReconnectStatus
} from "molecule/scheduler/SchedulerBloc";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { IconCloseCircle } from "src/constants/icons";
import reportErrorSentry from "src/lib/reportErrorSentry";
import translate from "src/lib/translate";
import AppointmentsBloc from "src/state/AppointmentsBloc/AppointmentsBloc";
import { tracker } from "src/state/state";
import TaskManagementBloc from "src/state/TaskManagementBloc/TaskManagementBloc";
import TrackCubit from "src/state/Track/TrackCubit";
import { TranslationKey } from "src/types/translationKey";
import AppointmentIcon from "../AppointmentIcon/AppointmentIcon";
import Avatar from "../Avatar/Avatar";
import BlockingLoadingOverlayController from "../BlockingLoadingOverlay/BlockingLoadingOverlayController";
import Loader from "../Loader/Loader";
import Translate from "../Translate/Translate";
import { extractErrorCode } from "src/lib/errors";
import {
  Heading,
  StyledSchedulerConfirmSlot,
  Wrap
} from "./AppointmentScheduler.styled";
import AppointmentSchedulerCubit from "./AppointmentSchedulerCubit";
import BookAppointmentText from "./BookAppointmentText";
import LabsHint from "./LabsHint";
import ReuseProviderScheduler from "./ReuseProviderScheduler";
import SchedulerEarlyState from "./SchedulerEarlyState";
import SelectProviderSection from "./SelectProviderSection";
import SkipVisitSection from "./SkipVisitSection";
import SlotAvailabilitySection from "./SlotAvailabilitySection";

const AppointmentScheduler: FC<{
  type?: ScheduleAppointmentTypes;
  rescheduleId?: string;
  reschedulingRequired?: boolean;
  /** `program/group/slug` of the task this scheduler was opened for, set only when the member is allowed to skip it. */
  skipTaskPath?: string;
  onScheduled?: () => void;
}> = ({
  type,
  onScheduled,
  rescheduleId,
  reschedulingRequired,
  skipTaskPath
}) => {
  const [, { hasIncompleteLabTask }] = useBlocNext(TaskManagementBloc, {
    dependencySelector: (state) => [state.tasks]
  });

  const [
    {
      loading,
      selectedDate,
      availableSlotGroups,
      selectedSlot,
      selectedSlotGroup,
      previousProvider,
      reconnectStatus
    },
    {
      setSelectedSlot,
      bookSelectedSlot,
      fetchPreviousProvider,
      handlePreferenceChangeReconnectProvider,
      showSelectProvider,
      getActiveStep
    }
  ] = useBloc(SchedulerBloc, {
    props: {
      availableSlots: [],
      onSelectDate: (date) =>
        tracker.track("Scheduler Date Selected", {
          data: {
            ...getCommonTrackingProps(),
            selectedDate: TrackCubit.formatDate(date)
          }
        }),
      onAcceptReconnectProvider: (reconnectStatus) =>
        tracker.track("Scheduler Reconnect Provider Accepted", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onRejectReconnectProvider: (reconnectStatus) =>
        tracker.track("Scheduler Reconnect Provider Rejected", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onPreviousProviderDismissed: (reconnectStatus) =>
        tracker.track("Scheduler Previous Provider Dismissed", {
          data: {
            ...getCommonTrackingProps(),
            reconnectStatus
          }
        }),
      onSelectSlotGroup: (slotGroup) =>
        tracker.track("Scheduler Time Selected", {
          data: {
            ...getCommonTrackingProps(),
            slotTime: TrackCubit.formatDateTime(slotGroup.dateTime),
            slotDuration: slotGroup.duration,
            slotProviderIds: slotGroup.slotProviderIds,
            slotProviderNames: slotGroup.slotProviderNames
          }
        }),
      onSelectSlot: (slot) =>
        tracker.track("Scheduler Provider Selected", {
          data: {
            ...getCommonTrackingProps(),
            autoSelected: slot.autoSelected ?? false,
            slotTime: TrackCubit.formatDateTime(slot.dateTime),
            slotDuration: slot.duration,
            slotProviderId: slot.slotProviderId,
            slotProviderName: slot.slotProviderName
          }
        }),
      onDeselectSlot: () =>
        tracker.track("Scheduler Confirmation Closed", {
          data: {
            ...getCommonTrackingProps()
          }
        }),
      onSelectProvider: (provider) =>
        tracker.track("Scheduler Provider Selected", {
          data: {
            ...getCommonTrackingProps(),
            providerId: provider.id,
            providerName: provider.name
          }
        }),
      onAppointmentBooked: (appointment) =>
        tracker.track("Scheduler Appointment Booked", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: appointment.id,
            appointmentDateTime: TrackCubit.formatDateTime(
              appointment.dateTime
            ),
            appointmentDuration: appointment.duration
          }
        }),
      onAppointmentRescheduled: (oldAppointment, newAppointment) =>
        tracker.track("Scheduler Appointment Rescheduled", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: newAppointment.id,
            oldAppointmentDateTime: TrackCubit.formatDateTime(
              oldAppointment.dateTime
            ),
            oldAppointmentDuration: oldAppointment.duration,
            newAppointmentDateTime: TrackCubit.formatDateTime(
              newAppointment.dateTime
            ),
            newAppointmentDuration: newAppointment.duration
          }
        })
    }
  });

  const [appointmentType, setAppointmentType] = useState<
    ScheduleAppointmentTypes | undefined
  >(type);
  const [rescheduleAppointment, setRescheduleAppointment] = useState<
    AppointmentResponse | undefined
  >();
  const [error, setError] = useState<TranslationKey | undefined>(
    SchedulerBloc.getInitialSchedulerError(type, rescheduleId)
  );
  const scheduleSuccess = useRef(false);
  const isMounted = useRef(true);
  const trackedOpen = useRef(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    Blac.getBloc(SchedulerBloc).reschedulingRequired = reschedulingRequired;
  }, [reschedulingRequired]);

  useEffect(() => {
    if (appointmentType && typeof previousProvider !== "undefined") {
      if (!trackedOpen.current) {
        tracker.track("Scheduler Opened", {
          data: {
            ...getCommonTrackingProps(),
            appointmentId: rescheduleId
          }
        });
        trackedOpen.current = true;
      }
      return () => {
        const useSelectedDate = selectedDate;
        const useSelectedSlotTime = selectedSlot?.from;
        setTimeout(() => {
          if (!scheduleSuccess.current && !isMounted.current) {
            tracker.track("Scheduler Abandoned", {
              data: {
                ...getCommonTrackingProps(),
                selectedDate: TrackCubit.formatDate(useSelectedDate),
                selectedSlotTime:
                  TrackCubit.formatDateTime(useSelectedSlotTime),
                activeStep: getActiveStep()
              }
            });
          }
        }, 100);
      };
    }
  }, [
    type,
    rescheduleId,
    appointmentType,
    previousProvider,
    selectedDate,
    selectedSlot,
    reconnectStatus
  ]);

  /**
   * requestProviders
   * @description
   * undefined means we haven't asked the question yet
   * false means we don't filter for providers
   * string[] means we filter for these providers
   */
  const [requestProviders, setRequestProviders] = useState<
    string[] | false | undefined
  >();

  const handleSetRequestProviders = (
    providers: string[] | false,
    action: SchedulerReconnectStatus
  ) => {
    setRequestProviders(providers);
    handlePreferenceChangeReconnectProvider(action);
  };

  const availabilitySectionRef = useRef<HTMLDivElement>(null);
  const headingRef = useRef<HTMLDivElement>(null);
  const slotProviderSectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (appointmentType && typeof previousProvider === "undefined") {
      void fetchPreviousProvider(appointmentType);
    }
  }, [appointmentType, previousProvider]);

  const participantsNames = useMemo(() => {
    if (!rescheduleAppointment) {
      return [];
    }
    return rescheduleAppointment.participants
      .map((p) => p.displayName)
      .filter(Boolean);
  }, [rescheduleAppointment]);

  // #region Scroll to top
  useEffect(() => {
    setTimeout(() => {
      if (headingRef.current) {
        headingRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, []);

  useEffect(() => {
    setTimeout(() => {
      if (availabilitySectionRef.current) {
        availabilitySectionRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, [selectedDate]);

  useEffect(() => {
    setTimeout(() => {
      if (slotProviderSectionRef.current) {
        slotProviderSectionRef.current.scrollIntoView({
          behavior: "smooth"
        });
      }
    }, 60);
  }, [selectedSlotGroup]);
  // #endregion

  // #region Handlers
  const handleDismissSlotDetailModal = () => {
    setSelectedSlot(undefined);
  };

  const handleBookAppointment = () => {
    BlockingLoadingOverlayController.startLoading({
      bg: "transparent",
      title: "appointment.loading.booking",
      message: "appointment.loading.booking.description"
    });

    void bookSelectedSlot({
      rescheduleAppointment
    })
      .then(async (data) => {
        Blac.getBloc(AppointmentsBloc).addAppointment(data);
        scheduleSuccess.current = true;
        if (onScheduled) {
          onScheduled();
        }
        const schedulerCubit = Blac.getBloc(AppointmentSchedulerCubit);
        const confirmationOptions =
          await schedulerCubit.resolveBookingConfirmationOptions(data);
        schedulerCubit.showBookingConfirmation(confirmationOptions);
      })
      .catch((e: unknown) => {
        reportErrorSentry(e);
        BlockingLoadingOverlayController.loadingError({
          title: translate("error_generic_title"),
          message: translate("appointment.scheduling.error", {
            context: extractErrorCode(e)
          }),
          retry: () => {
            handleBookAppointment();
          }
        });
      });
  };
  // #endregion

  useEffect(() => {
    let mounted = true;
    if (rescheduleId && !appointmentType && !rescheduleAppointment) {
      Blac.getBloc(AppointmentsBloc)
        .getAppointmentById(rescheduleId)
        .then((appointment) => {
          if (!mounted) {
            return;
          }
          if (!appointment) {
            setError("error_appointment_details_load_failed");
            return;
          }
          if (isDeprecatedAppointmentType(appointment.type)) {
            setError("error_appointment_details_load_failed");
            return;
          }
          setAppointmentType(appointment.type);
          setRescheduleAppointment(appointment);
        })
        .catch((e: unknown) => {
          reportErrorSentry(e);
        });
    }

    return () => {
      mounted = false;
    };
  }, [rescheduleId, appointmentType, rescheduleAppointment]);

  function getCommonTrackingProps() {
    return {
      mode: rescheduleId ? "reschedule" : "new",
      appointmentType,
      previousProviderId: previousProvider?.userId,
      previousProviderName: previousProvider?.displayName,
      reconnectStatus
    };
  }

  const earlyState = SchedulerEarlyState({
    error,
    appointmentType,
    previousProvider,
    rescheduleAppointment,
    requestProviders,
    onSetRequestProviders: handleSetRequestProviders
  });

  if (earlyState) {
    return earlyState;
  }

  if (!appointmentType) {
    return null;
  }

  // previousProvider === null means no earlier appointment of this type was
  // found, i.e. this is the member's first visit of this type. Members who've
  // already had one don't need the labs reminder again.
  const isFirstVisitOfType = previousProvider === null;
  const showLabsHint =
    !rescheduleId && isFirstVisitOfType && hasIncompleteLabTask;

  // Skipping is offered while the member is still deciding; once they pick a
  // date the slot list takes that spot and the nudge would compete with it.
  const skipVisitPath = selectedDate ? undefined : skipTaskPath;

  const titleContext = !rescheduleId
    ? "schedule"
    : participantsNames.length > 0
      ? "rescheduleNamed"
      : "reschedule";

  return (
    <Wrap>
      <Heading ref={headingRef}>
        <div className="appointment-icon">
          <AppointmentIcon type={appointmentType} />
        </div>
        <h2>
          {translate("appointment.title", {
            context: titleContext,
            name: translate("appointment.name", {
              context: appointmentType.toLowerCase().replace("_", "-")
            }),
            participants: participantsNames
          })}
        </h2>
        <LabsHint show={showLabsHint} />
      </Heading>

      <Loader active={loading}>
        <ReuseProviderScheduler
          previousProvider={previousProvider}
          requestProviders={requestProviders}
          onDismiss={() => handleSetRequestProviders(false, "dismissed")}
        />

        <Scheduler
          view="pick-date"
          type={appointmentType}
          rescheduleAppointment={rescheduleAppointment}
          requestProviders={requestProviders || undefined}
        />

        {skipVisitPath && <SkipVisitSection taskPath={skipVisitPath} />}

        <SlotAvailabilitySection
          selectedDate={selectedDate}
          availableSlotGroups={availableSlotGroups}
          sectionRef={availabilitySectionRef}
        />

        <SelectProviderSection
          show={Boolean(showSelectProvider)}
          sectionRef={slotProviderSectionRef}
        />

        <Modal
          variant="sheet"
          isOpen={Boolean(selectedSlot)}
          onDidDismiss={handleDismissSlotDetailModal}
          className="selected-search-result-info"
        >
          <button
            type="button"
            aria-label="Close"
            className="close-modal-button"
            onClick={handleDismissSlotDetailModal}
          >
            <IconCloseCircle />
          </button>

          <StyledSchedulerConfirmSlot>
            <Scheduler
              view="confirm-slot"
              avatar={(id) => <Avatar id={id} />}
              handleBookAppointment={handleBookAppointment}
              rescheduleAppointment={rescheduleAppointment}
              bookAppointmentText={
                <BookAppointmentText
                  isReschedule={Boolean(rescheduleAppointment)}
                />
              }
              scheduleInfo={
                <p>
                  <Translate msg="appointment.info.cancellation.policy" />
                </p>
              }
            />
          </StyledSchedulerConfirmSlot>
        </Modal>
      </Loader>
    </Wrap>
  );
};

export default AppointmentScheduler;
